import React, { useEffect, useState } from "react";
import axios from "axios";
import AppContext from "../Context/Context";
import axiosAuth from "../Axios/Interceptors";
import LoadingAnimation from "../LoadingAnimation/LoadingAnimation";
import { useGlobalState } from "../Context/GlobalStateContext";
import { showErrorMessage } from "../Shared/Toast";

interface Conversation {
  ConversationID: number;
  Subject: string;
  LastMessage: string;
  LastMessageTime: string;
  LastMessageTimeDisplay?: string;
  UnRead: number;
  ObjectType: number;
}

interface User {
  Id: number;
  Name: string;
  IsChecked: boolean;
}

interface Props {
  conversationID?: number;
  sendWAMessage?: boolean;
  childMessage?: string;
  onConversationSelect: (data: {
    Status: number;
    ConversationID: number;
  }) => void;
  isAdmin?: boolean;
}

const ConversationList: React.FC<Props> = ({
  conversationID = 0,
  sendWAMessage = false,
  childMessage,
  onConversationSelect,
}) => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<number>(0);
  const [availableHeight, setAvailableHeight] = useState<number>(
    window.innerHeight
  );
  const [showModal, setShowModal] = useState(false);
  const [conversationName, setConversationName] = useState("");
  const [isAnimation, setIsAnimation] = useState(false);
  const globalState = useGlobalState();
  /* ---------------- Resize ---------------- */
  useEffect(() => {
    const onResize = () => setAvailableHeight(window.innerHeight);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  /* ---------------- Load Conversations ---------------- */
  useEffect(() => {
    loadConversationList();
  }, [conversationID, childMessage]);

  const loadConversationList = async () => {
    const res = await axiosAuth.post(
      AppContext.APIURL + "api/Conversation/LoadConversationList",
      {
        ConversationID: conversationID,
      }
    );

    let data: Conversation[] = res.data[0];
    if (sendWAMessage) {
      data = data.filter((x) => x.ObjectType === 100);
    } else {
      data = data.filter((x) => x.ObjectType !== 100);
    }

    setConversations(data);

    if (data.length > 0 && selectedConversation === 0) {
      selectConversation(data[0].ConversationID);
    }

    updateTimestamps(data);
  };

  /* ---------------- Timestamp Logic ---------------- */
  const updateTimestamps = (data: Conversation[]) => {
    const now = new Date();

    data.forEach((c) => {
      const last = new Date(c.LastMessageTime);
      const diffSec = Math.floor((now.getTime() - last.getTime()) / 1000);
      const min = Math.floor(diffSec / 60);
      const hr = Math.floor(min / 60);
      const day = Math.floor(hr / 24);

      if (diffSec < 60) c.LastMessageTimeDisplay = "< 1m";
      else if (min < 60) c.LastMessageTimeDisplay = `${min}m`;
      else if (hr < 24) c.LastMessageTimeDisplay = `${hr}h`;
      else if (day < 7) c.LastMessageTimeDisplay = `${day}d`;
      else c.LastMessageTimeDisplay = last.toLocaleDateString();
    });

    setConversations([...data]);
  };

  /* ---------------- Select Conversation ---------------- */
  const selectConversation = (id: number) => {
    setSelectedConversation(id);
    onConversationSelect({ Status: 1, ConversationID: id });
  };

  /* ---------------- Load Users ---------------- */
  const loadUsers = async () => {
    let data = {} as any;
    data.FirstName = "";
    data.LastName = "";
    data.Email = "";
    setIsAnimation(true);
    const res = await axiosAuth.post(
      AppContext.APIURL + "api/Conversation/LoadUsersDataChats",
      data
    );
    const list = res.data[0].map((u: any) => ({
      Id: u.SiteUserID,
      Name: `${u.FirstName} ${u.LastName}`,
      IsChecked: false,
    }));
    setUsers(list);
    setIsAnimation(false);
  };

  const addConversation = async () => {
    if (!conversationName || conversationName == "") {
      showErrorMessage("Conversation Name Required");
      return;
    }

    const selectedUsers = users.filter((u) => u.IsChecked);

    const res = await axiosAuth.post(
      AppContext.APIURL + "api/Conversation/NewConversation",
      {
        Name: conversationName,
        UserList: selectedUsers,
      }
    );

    if (res.data > 0) {
      setShowModal(false);
      setConversationName("");
      loadConversationList();
    }
  };

  const deleteConversation = async (id: number) => {
    await axiosAuth.post(
      AppContext.APIURL + "api/Conversation/DeleteConversation",
      { ConversationID: id }
    );
    loadConversationList();
  };

  return (
    <>
      {isAnimation && <LoadingAnimation />}
      <div className="mn-convo-3">
        <span>My Conversations</span>
        {!sendWAMessage && (
          <a
            className="flex-d"
            onClick={() => {
              loadUsers();
              setShowModal(true);
            }}
          >
            <img src="/images/add.svg" width={20} height={20} />
          </a>
        )}
      </div>

      <div
        className="mn-convo-3a mn-scroll-main"
        style={{ height: availableHeight - 220 }}
      >
        {conversations.length === 0 && (
          <h3 className="mn-convo-10">No conversations</h3>
        )}

        {conversations.map((item) => (
          <div
            key={item.ConversationID}
            className={`mn-convo-4 ${item.UnRead > 0 ? "unreadmessages" : ""}`}
          >
            <a
              className={`mn-convo-5 font-13 ${
                selectedConversation === item.ConversationID
                  ? "selectedconvo"
                  : ""
              }`}
              onClick={() => selectConversation(item.ConversationID)}
            >
              <div className="mn-convo-9">
                <span className="mn-convo-6">{item.Subject}</span>
                <div className="flex-da flex-gap10">
                  {globalState.RolesList.RoleName == "Admin" &&
                    selectedConversation == item.ConversationID && (
                      <a
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteConversation(item.ConversationID);
                        }}
                      >
                        <img src="/images/delete.png" width={14} height={14} />
                      </a>
                    )}
                  {item.LastMessage && (
                    <span className="mn-convo-7">
                      {item.LastMessageTimeDisplay}
                    </span>
                  )}
                </div>
              </div>

              {item.LastMessage && (
                <span className="mn-convo-8">{item.LastMessage}</span>
              )}
            </a>
          </div>
        ))}
      </div>

      {showModal && (
        <div className="popup-help popup-help-index">
          <div className="help-popup-display">
            <div className="help-inner sm-popup">
              <div className="help-head flex-da justify-btw">
                <h3 className="bold-font flex-d m-all0">New Conversation</h3>
                <a className="close-help" onClick={() => setShowModal(false)}>
                  <img
                    src="/images/close.svg"
                    width="15px"
                    height="15px"
                    alt="cross"
                  />
                </a>
              </div>

              <div className="grid-d flex-gap10 mt-20">
                <div className="field-input">
                  <input
                    className="input-field w-100"
                    placeholder="Conversation Name"
                    value={conversationName}
                    onChange={(e) => setConversationName(e.target.value)}
                  />
                </div>
                <span className="mt-10 font-16 bold-font">Users</span>
                <div
                  className="grid-d flex-gap10"
                  style={{ maxHeight: 300, overflow: "auto" }}
                >
                  {users.map((u) => (
                    <div
                      key={u.Id}
                      className="flex-d flex-ac flex-gap10 font-12"
                    >
                      <input
                        type="checkbox"
                        checked={u.IsChecked}
                        onChange={() =>
                          setUsers(
                            users.map((x) =>
                              x.Id === u.Id
                                ? { ...x, IsChecked: !x.IsChecked }
                                : x
                            )
                          )
                        }
                      />
                      {u.Name}
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex-daj btn-start gap-10 mt-20">
                <a className="btn-primary flex-daj" onClick={addConversation}>
                  Save
                </a>

                <a className="btn-primary flex-daj" onClick={addConversation}>
                  Cancel
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ConversationList;
